import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  createDocumentFile
} from '../api';
import { FileObjectValues } from '../components/fileDetailPanel/types';
import type { DocumentFile, DocumentFileObject } from '../types';
import {
  appendFileToInfiniteData,
  fileMatchesSearch,
  updateFileInInfiniteData,
  type InfiniteFolderContentsData,
} from './folderMutationCache';
import { folderContentsKeys } from './useFolderContents';

export interface CreateDocumentFileMutationInput {
  folderId: string | null;
  name: string;
  category: number;
  companyLevelId?: string;
  deviceModelId?: number;
  userLevel?: number;
  fileType?: string;
}

export interface UploadFileObjectMutationInput {
  // The parent file record id
  fileId: string;
  // Row id used to report progress back to the UI
  rowId: string;
  values: FileObjectValues;
  file: File;
  onProgress?: (rowId: string, progress: number, objectId: string) => void;
}

export interface UploadFileObjectMutationResult {
  objectId: string;
}

interface UseFileMutationsOptions {
  companyId: string | null;
  currentFolderId?: string;
  search?: string;
}

export function useFileMutations({
  companyId,
  currentFolderId,
  search,
}: UseFileMutationsOptions) {
  const queryClient = useQueryClient();
  const infiniteQueryKey = companyId
    ? folderContentsKeys.infinite(companyId, currentFolderId, { search })
    : null;

  // Patches a single file entry inside the infinite query cache.
  const updateScopedFile = useCallback(
    (fileId: string, updater: (file: DocumentFile) => DocumentFile) => {
      if (!infiniteQueryKey) return;

      queryClient.setQueryData(infiniteQueryKey, (data: unknown) =>
        updateFileInInfiniteData(data as never, fileId, updater)
      );
    },
    [infiniteQueryKey, queryClient]
  );

  // Appends a new file entry to the infinite query cache if it matches the current search.
  const appendScopedFile = useCallback(
    (file: DocumentFile) => {
      if (!infiniteQueryKey) return;

      queryClient.setQueryData(infiniteQueryKey, (data: unknown) => {
        const currentData = data as InfiniteFolderContentsData;

        if (!fileMatchesSearch(file.name, search)) {
          return currentData;
        }

        return appendFileToInfiniteData(currentData, file, search);
      });
    },
    [infiniteQueryKey, queryClient, search]
  );

  // Upserts a single DocumentFileObject inside its parent file entry in the cache.
  const upsertFileObject = useCallback(
    (fileId: string, overrides?: Partial<DocumentFileObject>, fileObject?: DocumentFileObject,) => {
      const nextObject = { ...fileObject, ...overrides };

      updateScopedFile(fileId, (file: DocumentFile) => {

        let found = false
        let fileObjects = [
          ...(file.fileObjects ?? []).map((obj) => {
            if (obj.id === nextObject.id) {
              found = true;
              return { ...obj, ...overrides }
            }
            return obj
          }
          ),
        ]

        if (!found && fileObject) {
          fileObjects.push({ ...fileObject, ...overrides })
        }

        return {
          ...file,
          fileObjects: fileObjects,
        }
      });

      return nextObject;
    },
    [updateScopedFile]
  );

  // Creates the document file record on the server and adds it to the cache immediately
  // with an uploading state so the UI can show progress before any file objects are uploaded.
  const createFileMutation = useMutation({
    mutationFn: async (input: CreateDocumentFileMutationInput): Promise<DocumentFile> => {
      const createdFile = await createDocumentFile({
        folderId: input.folderId,
        name: input.name,
        category: input.category,
        fileType: input.fileType,
        companyLevel: input.companyLevelId,
        deviceModelId: input.deviceModelId,
        userLevel: input.userLevel,
      });

      appendScopedFile({
        ...createdFile,
        fileObjects: [],
        uploadState: { status: 'uploading', progress: 0 },
      });

      return createdFile;
    },
  });

/*   // Fetches the upload URL, streams the binary to storage, and marks the object as available.
  const uploadAndMarkFileObjectAvailable = useCallback(
    async (
      fileId: string,
      fileObjId: string,
      file: File,
      rowId: string,
      abortController: AbortController,
      onProgress?: (rowId: string, progress: number, objectId: string) => void
    ) => {
      const uploadUrlResponse = await getDocumentFileUploadUrl(
        fileId,
        fileObjId,
        file.name
      );

      try {
        await uploadDocumentFileBinary(
          uploadUrlResponse.url,
          file,
          (progress) => onProgress?.(rowId, progress, fileObjId),
          abortController.signal
        );

        await setStoredFileActive(fileId, fileObjId, uploadUrlResponse.storedFileId);
      } catch (error) {
        abortController.abort();
        throw error;
      }

      upsertFileObject(fileId, { isAvailable: true });
    },
    [upsertFileObject]
  ); */

/*   // Creates a single file object record, gets an upload URL, uploads the binary,
  // and marks the object as available — all for one row at a time.
  // Can be called multiple times in parallel or sequentially for multi-language files.
  const uploadFileObjectMutation = useMutation({
    mutationFn: async ({
      fileId,
      rowId,
      values,
      file,
      onProgress,
    }: UploadFileObjectMutationInput): Promise<UploadFileObjectMutationResult> => {
      const abortController = new AbortController();

      const resolvedFileObject = await createDocumentFileObject(fileId, {
        languageId: values.languageId,
        fileSize: file.size,
        name: values.name,
      });

      upsertFileObject(fileId, {
        fileId,
        isAvailable: false,
      }, resolvedFileObject);

      await uploadAndMarkFileObjectAvailable(
        fileId,
        resolvedFileObject.id,
        file,
        rowId,
        abortController,
        onProgress
      );

      return { objectId: resolvedFileObject.id };
    },
  }); */

  return { createFileMutation, updateScopedFile, upsertFileObject, appendScopedFile };
}
